import { ReactNode } from 'react';
import { CodeType } from '../CodeType';
import { BuildCodeParams, CodeSample } from './CodeSample';
import Paragraph from 'antd/lib/typography/Paragraph';
import Text from 'antd/lib/typography/Text';
import React from 'react';
import CodeBlock from '../CodeBlock';

export default class MobileAndroidSample extends CodeSample {
  name = 'Android';
  language = 'kotlin';
  codeType = CodeType.MOBILE;
  tabIcon = '';
  commentChar = undefined;

  buildCode({ environment, notification, user }: BuildCodeParams): ReactNode {
    const buildGradle = `allprojects {
    repositories {
        ...
        maven { url 'https://jitpack.io' }
    }
}`;

    const dependency = `dependencies {
    implementation 'com.github.notificationapi:notificationapi-android-sdk:1.0.0'
}`;

    const service = `class MyService: NotificationApiService() {
        
// Handle FCM tokens whenever they are generated
override fun onNewToken(token: String) {
    super.onNewToken(token)
}

// Build notifications before displaying them to the user
override fun onPreDisplayNotification(message: RemoteMessage) {
    val onClickIntent = NotificationApiIntent(this, MainActivity::class.java, message)

    displayNotification(
        intent = onClickIntent,
        icon = android.R.drawable.ic_dialog_info
    )
}`;

    const manifest = `<service
    android:name=".MyService"
    android:exported="false">
    <intent-filter>
        <action android:name="com.google.firebase.MESSAGING_EVENT" />
    </intent-filter>
</service>`;

    const code = `class MainActivity : NotificationApiActivity() {
    companion object {
        const val TAG = "Example App"
    }
    
    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        setContentView(R.layout.activity_main)

        // Configure NotificationAPI with credentials
        NotificationApi.shared.configure(
            NotificationApiCredentials(
            clientId = "${environment?.clientId}",
            userId = "${user.email}"
            )
        )

        // Request the user's permission to use notifications
        NotificationApi.shared.askNotificationPermissions()
    }

    // Handle clicked notifications
    override fun onNotificationClicked(message: RemoteMessage) {
        super.onNotificationClicked(message)

        Log.d(TAG, "Notification was clicked on")
    }

    // Handle notification permission request results
    override fun onNotificationRequestPermissionResult(isGranted: Boolean) {
        super.onNotificationRequestPermissionResult(isGranted)
        Log.d(TAG, "Notifications are authorized?: $isGranted")
    }
}`;

    return (
      <>
        <Paragraph style={{ paddingTop: 10 }}>
          Create FCM key (see{' '}
          <a href="https://docs.notificationapi.com/guides/mobile-push#firebase-cloud-messaging-fcm">
            our guide here
          </a>
          ) for your project.
        </Paragraph>
        <Paragraph>
          Add JitPack to your <Text code>build.gradle</Text>.
          <CodeBlock language="groovy" code={buildGradle} />
        </Paragraph>
        <Paragraph>
          Add NotificationAPI Android SDK as a dependency.
          <CodeBlock language="groovy" code={dependency} />
        </Paragraph>
        <Paragraph>
          Create a new service that extends from{' '}
          <Text code>NotificationApiService</Text>
          <CodeBlock language="kotlin" code={service} />
        </Paragraph>
        <Paragraph>
          Add your service to <Text code>AndroidManifest.xml</Text>
          <CodeBlock language="xml" code={manifest} />
        </Paragraph>
        <Paragraph>
          Create a new Activity that extends from{' '}
          <Text code>NotificationApiActivity</Text>
        </Paragraph>
        <CodeBlock language={this.language} code={code} />
      </>
    );
  }
}
